const toAmount = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const hasMatterId = (matterId) => matterId !== '';

// ListItem entries also carry a legacy `MatterID` (capital D) casing on older
// data — see the same fallback in `mileage-validation.js:resolveMatterIdFromJson`
// and `expense.js`. Missing this would undercount matters and silently
// suppress the notice on a receipt that genuinely needs it. Stringified and
// trimmed for the same reason `_view_id` and `checksum` are compared as
// strings below — a numeric `MatterId` on one line and a string form on
// another must not count as two matters.
const matterIdOf = (item) => String(item.MatterId ?? item.MatterID ?? '').trim();

/**
 * Computes the shared-receipt context for one invoice line, so the approval
 * modal can explain that this line is only part of a larger receipt that was
 * split across several client matters.
 *
 * Groups sibling lines by `checksum` — the same receipt-fingerprint tag the
 * modal's delete flow already groups by (see the line-item delete handler in
 * `invoice-approval-modal.js`). Rule 3 below excludes mileage entries
 * separately: their `checksum` is a whole-second timestamp rather than a
 * content fingerprint (stamped in xpens-api's `expenses_accounts_service`),
 * so two mileage entries saved in the same second could otherwise collide.
 * If a guaranteed-unique split identifier ships (see the unscheduled design
 * at `../xpens-designs/unticketed/matter-split-coherence/XPEN-XXXX-matter-split-coherence-technical-design.md`
 * §5.2), group by that instead of `checksum`.
 *
 * Deliberately returns only `receiptTotal` and `currentAmount` — never
 * another matter's identifier, amount, or count. That is a product decision
 * (XPEN-2935): the approval UI must never display who else the receipt was
 * shared with. This is a UI-display boundary, not a data-access boundary —
 * `invoiceJson` itself already holds every sibling line before this function
 * ever runs; keeping the return value minimal only prevents this helper's
 * own callers from accidentally rendering another matter's data.
 *
 * @param {object} invoiceJson - The saved invoice JSON (`invoice.json`), holding `ListItem`.
 * @param {string|number} viewId - The `_view_id` of the line item being approved.
 * @returns {null|{receiptTotal: number, currentAmount: number}} The receipt's full amount
 *   and this line's own amount, or `null` when the line is not part of a receipt shared
 *   between two or more matters.
 */
export const buildSharedReceiptContext = (invoiceJson, viewId) => {
    const items = invoiceJson?.ListItem || [];
    const matchedItem = items.find((item) => String(item._view_id) === String(viewId));
    if (!matchedItem) return null;
    if (!matchedItem.checksum) return null;
    if (matchedItem._expense_type === 'mileage') return null;

    const siblings = items.filter((item) => String(item.checksum) === String(matchedItem.checksum));
    const matterIds = new Set(siblings.map(matterIdOf).filter(hasMatterId));
    if (matterIds.size < 2) return null;

    return {
        receiptTotal: siblings.reduce((sum, item) => sum + toAmount(item.Total), 0),
        currentAmount: toAmount(matchedItem.Total),
    };
};

export default buildSharedReceiptContext;
